
import NavigationButtons from '@/components/NavigationButtons';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import RelatedLinks from '@/components/RelatedLinks';

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Deep Seek AI | Leading AI Innovation & Research</title>
        <meta name="description" content="Learn about Deep Seek AI's mission to advance artificial intelligence through ethical research, innovative products, and commitment to excellence." />
        <link rel="canonical" href="https://deepseek.ai/about" />
        
        <meta property="og:title" content="About Deep Seek AI | Leading AI Innovation & Research" />
        <meta property="og:description" content="Learn about Deep Seek AI's mission to advance artificial intelligence through ethical research, innovative products, and commitment to excellence." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://deepseek.ai/about" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Deep Seek AI",
            "url": "https://deepseek.ai",
            "logo": "https://deepseek.ai/logo.png",
            "description": "Deep Seek AI is dedicated to pushing the boundaries of artificial intelligence to create solutions that empower businesses and enhance human capabilities."
          })}
        </script>
      </Helmet>

      <div className="min-h-screen pt-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-deepseek-secondary mb-8">About Deep Seek AI</h1>
          
          <NavigationButtons />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-deepseek-primary">Our Mission</h2>
              <p className="text-lg text-gray-600">
                At Deep Seek AI, we're dedicated to pushing the boundaries of artificial intelligence to create solutions that 
                empower businesses and enhance human capabilities. Our mission is to make advanced AI technology accessible, 
                ethical, and impactful.
              </p>
              <p className="text-lg text-gray-600">
                Through continuous innovation and research, we develop AI products that solve real-world problems. Our flagship 
                <Link to="/deepseek" className="text-blue-600 hover:underline"> Deep Seek technology</Link> combines powerful language models and search capabilities to transform how people access and utilize information.
              </p>
              <h2 className="text-2xl font-semibold text-deepseek-primary pt-4">Our Vision</h2>
              <p className="text-lg text-gray-600">
                We envision a future where AI seamlessly integrates with human expertise, creating a more efficient, 
                innovative, and sustainable world. Through continuous research and development, we strive to be at the 
                forefront of AI advancement.
              </p>
              <p className="text-lg text-gray-600">
                Our latest developments with <Link to="/deepseek-r1" className="text-blue-600 hover:underline">Deep Seek R1</Link> and the upcoming 
                <Link to="/blog/deepseek-r2-challenges" className="text-blue-600 hover:underline"> R2 release</Link> demonstrate our commitment to this vision.
              </p>
            </div>
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-deepseek-primary">Our Values</h2>
              <ul className="space-y-4 text-lg text-gray-600">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Innovation:</span> Constantly pushing the boundaries of what's possible in <Link to="/blog/what-is-deep-learning-ai" className="text-blue-600 hover:underline">deep learning</Link> and AI
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Ethics:</span> Ensuring responsible AI development and deployment
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Collaboration:</span> Working together to achieve breakthrough results
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Excellence:</span> Maintaining the highest standards in all we do
                </li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-deepseek-primary pt-6">Our Team</h2>
              <p className="text-lg text-gray-600">
                Deep Seek AI was founded by a team of leading AI researchers and engineers with backgrounds from top institutions worldwide. Our diverse team brings together expertise in natural language processing, machine learning, and software development.
              </p>
              <p className="text-lg text-gray-600">
                We're united by our passion for AI innovation and our commitment to creating technology that makes a positive impact on society.
              </p>
            </div>
          </div>
          
          <div className="mt-16">
            <h2 className="text-2xl font-semibold text-deepseek-primary mb-6">Learn More</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <RelatedLinks />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
